import { AccountSelector, Input, Select } from '@components'
import { VatCode, ACC_TYPES_SALES_AND_BALANCE_SHEET } from '@constants'
import { useBusinessContext } from '@context'
import { createProduct, IProduct, updateProduct } from '@query'
import { Switch } from '@components/ui/Primitives'
import { MonetaryAmount } from '@components/MonetaryAmount/MonetaryAmount'
import { setAPIErrors } from '@utils'
import React, { useEffect } from 'react'
import { Controller, get, useForm, useWatch } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getVatOptions } from '@utils/getVatOptions.tsx'
import { useVATCodes } from '@root/query/hooks/useVATCodes'
import styled from 'styled-components'

interface Props {
  product?: Partial<IProduct>
  onSubmit?: () => void
  onCreate?: (data: Partial<IProduct>) => void
}

export const ProductForm: React.FC<Props> = ({ product, onSubmit: onSubmitCallback, onCreate }) => {
  const [t] = useTranslation()
  const { slug, data: business } = useBusinessContext()
  const queryClient = useQueryClient()
  const { data: vatCodeOptions } = useVATCodes()

  const defaultValues = React.useMemo(
    () => ({
      vat_code: VatCode.DOMESTIC_SALES_TAX,
      vat_rate: getVatOptions(business.country_config)?.[0].rate,
      ...product
    }),
    [product]
  )

  const {
    register,
    handleSubmit,
    setError,
    control,
    setValue,
    formState: { errors }
  } = useForm<Partial<IProduct>>({
    defaultValues
  })

  const commonOptions = {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [slug, 'products'] })
    },
    onError: error => {
      const { status, data } = (error as any) ?? {}
      if (status === 400) {
        setAPIErrors(data, setError)
      } else {
        toast.error(t('general.error'))
      }
    }
  }

  const { mutateAsync: updateMutation } = useMutation<unknown, unknown, Partial<IProduct>>({
    mutationFn: data => updateProduct({ slug, productId: product?.id }, data),
    ...commonOptions
  })

  const { mutateAsync: createMutation } = useMutation<unknown, unknown, Partial<IProduct>>({
    mutationFn: data => createProduct({ slug }, data),
    ...commonOptions
  })

  const watchedValues = useWatch({
    control,
    defaultValue: defaultValues
  })
  const unit = watchedValues?.unit
  const vatCode = watchedValues?.vat_code
  const vatCodeOption = vatCodeOptions?.find(option => option.value === vatCode)
  const amount = Number(watchedValues?.amount) || 0
  const isVatInclusive = Boolean(watchedValues?.is_vat_inclusive)
  const vatRate = vatCodeOption?.tags.includes('taxfree') ? 0 : Number(watchedValues?.vat_rate) || 0

  const roundAmount = (value: number) => Math.round((value + Number.EPSILON) * 1000) / 1000
  const vatMultiplier = 1 + vatRate / 100

  const vatExclusiveAmount = roundAmount(isVatInclusive ? amount / vatMultiplier : amount)
  const vatInclusiveAmount = roundAmount(isVatInclusive ? amount : amount * vatMultiplier)

  const onSubmit = handleSubmit(async data => {
    const promise = await (product?.id ? updateMutation(data) : createMutation(data))
    onSubmitCallback && onSubmitCallback()
    onCreate && onCreate(await promise)
  })

  useEffect(() => {
    if (vatCodeOption?.tags.includes('taxfree')) {
      setValue('vat_rate', 0)
    } else {
      setValue('vat_rate', defaultValues?.vat_rate)
    }
  }, [vatCodeOption])

  return (
    <div onSubmit={event => event.stopPropagation()}>
      <FormRoot id="product-form" onSubmit={onSubmit}>
        <Input
          label={t('products.form.name')}
          errors={errors?.name}
          info={t('products.form.nameInfo')}
          required={true}
          {...register('name', {
            required: {
              value: true,
              message: t('validation.required')
            }
          })}
        />

        <Input
          label={t('products.form.code')}
          info={t('products.form.codeInfo')}
          errors={errors?.code}
          {...register('code')}
        />

        <Input
          label={t('products.form.description')}
          info={t('products.form.descriptionInfo')}
          errors={errors?.description}
          {...register('description')}
        />

        <br />

        <Select
          label={t('products.form.vatCode')}
          type="number"
          info={t('products.form.vatCodeInfo')}
          errors={errors?.vat_code}
          required={true}
          {...register('vat_code', {
            valueAsNumber: true,
            required: {
              value: true,
              message: t('validation.required')
            }
          })}
        >
          {vatCodeOptions
            ?.filter(({ tags }) => tags.includes('sales'))
            .map(({ label, value }) => (
              <option key={`option-${value}`} value={value}>
                {t(label)}
              </option>
            ))}
        </Select>

        {!vatCodeOption?.tags.includes('taxfree') && (
          <Select
            label={t('products.form.vatRate')}
            type="number"
            info={t('products.form.vatRateInfo')}
            errors={errors?.vat_rate}
            required={true}
            {...register('vat_rate', {
              valueAsNumber: true,
              shouldUnregister: true,
              required: {
                value: true,
                message: t('validation.required')
              }
            })}
          >
            {getVatOptions(business.country_config).map(option => (
              <option key={option.rate} value={option.rate}>{`${option.rate} %`}</option>
            ))}
          </Select>
        )}

        <Input
          label={t('products.form.unit')}
          errors={errors?.unit}
          info={t('products.form.unitInfo')}
          required={true}
          {...register('unit', {
            required: {
              value: true,
              message: t('validation.required')
            }
          })}
        />

        <Input
          label={t('products.form.unitAmount') + ' € / ' + (unit || '-')}
          type="number"
          step=".001"
          info={t('products.form.unitAmountInfo')}
          required={true}
          errors={errors?.amount}
          {...register('amount', {
            valueAsNumber: true,
            required: {
              value: true,
              message: t('validation.required')
            }
          })}
        />

        <Controller
          name="is_vat_inclusive"
          control={control}
          render={({ field: { onChange, value } }) => {
            return (
              <SwitchRow>
                <label htmlFor="is_vat_inclusive">
                  <SwitchLabel>{t('products.form.isVatInclusive')}</SwitchLabel>
                </label>
                <Switch
                  id="is_vat_inclusive"
                  height={24}
                  onChange={e => onChange(e.currentTarget.checked)}
                  checked={value ?? false}
                />
              </SwitchRow>
            )
          }}
        />

        <AmountPreviewContainer>
          <AmountPreviewRow>
            <AmountPreviewLabel>{t('products.form.amountExclVat')}</AmountPreviewLabel>
            <MonetaryAmount
              value={vatExclusiveAmount}
              precision={{
                maximumFractionDigits: 3,
                minimumFractionDigits: 2
              }}
            />
          </AmountPreviewRow>
          <AmountPreviewRow>
            <AmountPreviewLabel>{t('products.form.totalAmount')}</AmountPreviewLabel>
            <MonetaryAmount
              value={vatInclusiveAmount}
              precision={{
                maximumFractionDigits: 3,
                minimumFractionDigits: 2
              }}
            />
          </AmountPreviewRow>
        </AmountPreviewContainer>

        {business?.has_accrual_based_entries_for_invoicing && (
          <Controller
            control={control}
            name={'account_id'}
            render={({ field: { ref, value, onChange }, fieldState: { error } }) => (
              <>
                <AccountSelector
                  id={`product-account-select`}
                  label={t('products.form.account')}
                  info={t('products.form.accountInfo')}
                  ref={ref}
                  error={error}
                  value={value}
                  onChange={onChange}
                  errors={get(errors, 'account_id')}
                  accountFilter={{ type: ACC_TYPES_SALES_AND_BALANCE_SHEET }}
                  allowCreate={true}
                />
              </>
            )}
          />
        )}
      </FormRoot>
    </div>
  )
}

const FormRoot = styled.form`
  overflow: hidden;
`

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md}rem;
  margin-top: 16px;
  margin-bottom: 16px;
  padding: 8px;
`

const SwitchLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const AmountPreviewContainer = styled.div`
  margin-bottom: 16px;
`

const AmountPreviewRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`

const AmountPreviewLabel = styled.span`
  color: ${({ theme }) => theme.colors.neutralBlack};
`
